import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AcceptInvitationHeader } from '@/features/partners/components/layouts/accept-partner-invitation-header';
import { useAcceptSubprojectInvitationStore } from '@/features/partners/store/use-accept-subproject-invitation-store';
import { useCategoriesQuery } from '@/features/projects/categories/hooks/use-categories-query';
import {
  FlowBackButton,
  FlowSubmitButton,
} from '@/shared/components/flow/flow-actions';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

const formSchema = (
  t: TFunction<'subproject', 'AcceptInvitation.SubcategoryForm'>,
) =>
  z.object({
    categoryId: z.string().min(1, t('form_field.category.required')),
    subcategoryId: z.string().min(1, t('form_field.subcategory.required')),
  });

export const AcceptInvitationSubcategoryPage = () => {
  const { t } = useTranslation('subproject', {
    keyPrefix: 'AcceptInvitation.SubcategoryForm',
  });
  const { t: level2T } = useTranslation('categories', {
    keyPrefix: 'Level2',
  });
  const { t: level3T } = useTranslation('categories', {
    keyPrefix: 'Level3',
  });

  const { data: acceptData, setData: setAcceptData } =
    useAcceptSubprojectInvitationStore();
  const { data: categoriesQueryData } = useCategoriesQuery();
  const categories = categoriesQueryData?.categories;

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: {
      categoryId: acceptData.selectedCategory?.id || '',
      subcategoryId: acceptData.selectedSubcategory?.id || '',
    },
  });

  const selectedCategoryId = form.watch('categoryId');
  const availableSubcategories = categories?.level3;

  function onSubmit() {
    const values = form.getValues();

    const selectedSubcategory = values.subcategoryId
      ? categories?.level3.find((cat) => cat.id === values.subcategoryId)
      : undefined;

    setAcceptData({
      selectedSubcategory,
      step: 'budgets',
    });
  }

  return (
    <>
      <AcceptInvitationHeader />
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  name="categoryId"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue('subcategoryId', '');
                        }}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              asLabelInput
                              label={t('category_label')}
                            >
                              {field.value ? level2T(field.value as any) : null}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories?.level2.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {level2T(category.id as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="subcategoryId"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={
                          !selectedCategoryId || !availableSubcategories?.length
                        }
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              placeholder={t('subcategory_placeholder')}
                              asLabelInput
                              label={t('subcategory_label')}
                            >
                              {field.value ? level3T(field.value as any) : null}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableSubcategories?.map((subcategory) => (
                            <SelectItem
                              key={subcategory.id}
                              value={subcategory.id}
                            >
                              {level3T(subcategory.id as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <FlowBackButton
                onClick={() => {
                  setAcceptData({
                    step: 'category',
                  });
                }}
              >
                {t('back_button_label')}
              </FlowBackButton>
              <FlowSubmitButton>{t('submit_button_label')}</FlowSubmitButton>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
